import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@viastud/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@viastud/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@viastud/ui/form'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { Input } from '@viastud/ui/input'
import { trpc } from '@viastud/ui/lib/trpc'
import { PhoneInput } from '@viastud/ui/phone-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@viastud/ui/select'
import { ConfirmLeaveModal } from '@viastud/ui/shared/confirm-leave-modal'
import { InlineOtpPopover } from '@viastud/ui/shared/inline-opt-popover'
import { useSmsCaptcha } from '@viastud/ui/shared/sms-captcha'
import type { BaseFormModalProps } from '@viastud/ui/types/base-form-modal'
import { type AddChildSchema, addChildSchema } from '@viastud/utils'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useShallow } from 'zustand/shallow'

import { useAuthStore } from '@/store/auth.store'

interface AddChildModalProps extends BaseFormModalProps {
  isDirty: boolean
}

export const AddChildModal = ({ refresh, isDirty }: AddChildModalProps) => {
  const { requestToken, captchaDialog } = useSmsCaptcha()
  const phoneAnchorRef = React.useRef<HTMLDivElement>(null)
  const { parent } = useAuthStore(
    useShallow((state) => ({
      parent: state.user,
    }))
  )

  const { toast } = useToast()
  const [isPhoneOpen, setIsPhoneOpen] = useState(false)
  const [isPhoneVerified, setIsPhoneVerified] = useState(false)

  const { mutateAsync: sendSMSCodeMutate } = trpc.userAuth.sendPhoneVerificationCode.useMutation()
  const { mutateAsync: verifySMSCodeMutate } = trpc.userAuth.verifyPhoneCode.useMutation()

  const addChildForm = useForm<AddChildSchema>({
    resolver: zodResolver(addChildSchema),
    defaultValues: {
      lastName: '',
      firstName: '',
      email: '',
      phoneNumber: '',
      parentId: parent?.id ?? '',
      grade: 'TROISIEME',
    },
  })

  const [open, setOpen] = useState<boolean>(false)
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const sendSMSCode = async (phoneNumber: string) => {
    try {
      await sendSMSCodeMutate({
        phoneNumber,
        captchaToken: await requestToken(),
      })
      setIsPhoneVerified(false)
      setIsPhoneOpen(true)
      toast({
        title: 'Un code a été envoyé par SMS',
      })
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Une erreur est survenue',
          description: error.message,
          variant: 'destructive',
        })
      }
    }
  }

  const verifySMSCode = async (phoneNumber: string, otp: string) => {
    try {
      const result = await verifySMSCodeMutate({
        phoneNumber,
        code: otp,
      })
      if (result?.success) {
        setIsPhoneOpen(false)
        setIsPhoneVerified(true)
        toast({
          title: 'Numéro de téléphone vérifié avec succès',
        })
      }
    } catch {
      toast({
        title: 'Le code est invalide, veuillez réessayer',
        variant: 'destructive',
      })
    }
  }

  const { mutateAsync: addUserMutation, isPending } = trpc.user.createChild.useMutation({
    onSuccess: () => {
      setOpen(false)
      refresh()
      addChildForm.reset()
      setApiError(null)
      setIsPhoneVerified(false)
    },
    onError: (error) => {
      setApiError(error.message || "Une erreur est survenue lors de l'ajout de l'enfant")
    },
  })

  const onSubmit = async (data: AddChildSchema) => {
    if (!isPhoneVerified) {
      toast({
        title: 'Veuillez vérifier le numéro de téléphone avant de continuer',
        variant: 'destructive',
      })
      return
    }

    setApiError(null)
    try {
      await addUserMutation(data)
    } catch {
      // L'erreur est déjà gérée dans onError du mutation
    }
  }

  const handleClick = () => {
    if (isDirty) {
      setConfirmModalOpen(true)
    } else {
      setOpen(true)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setApiError(null)
    addChildForm.reset()
    setIsPhoneVerified(false)
    setIsPhoneOpen(false)
  }

  return (
    <>
      {captchaDialog}
      <ConfirmLeaveModal
        open={confirmModalOpen}
        title="Vous avez des modifications non sauvegardées pour vos enfants. Ceci annulera les changements. Êtes-vous sûr de vouloir continuer ?"
        cancel={() => {
          setConfirmModalOpen(false)
        }}
        confirm={() => {
          setConfirmModalOpen(false)
          setOpen(true)
        }}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <Button variant="default" className="min-h-9 min-w-9 rounded-full" onClick={handleClick}>
          <p className="font-semibold">Ajouter un enfant</p>
        </Button>
        <DialogContent>
          <Form {...addChildForm}>
            <form onSubmit={addChildForm.handleSubmit(onSubmit)}>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-950">
                  Ajouter un enfant
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-start gap-4 self-stretch py-4">
                {apiError && (
                  <div className="w-full rounded-md bg-red-50 p-3">
                    <p className="text-sm text-red-600">{apiError}</p>
                  </div>
                )}
                <FormField
                  control={addChildForm.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col">
                      <FormLabel className="text-sm font-medium text-gray-700">Nom</FormLabel>
                      <FormControl>
                        <Input className="w-full" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={addChildForm.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col">
                      <FormLabel className="text-sm font-medium text-gray-700">Prénom</FormLabel>
                      <FormControl>
                        <Input className="w-full" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={addChildForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col">
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Adresse e-mail
                      </FormLabel>
                      <FormControl>
                        <Input className="w-full" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={addChildForm.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col">
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Numéro de téléphone
                      </FormLabel>
                      <FormControl>
                        <div ref={phoneAnchorRef} className="relative w-full">
                          <PhoneInput
                            className="w-full"
                            placeholder="Numéro de téléphone"
                            rightButton={
                              <button
                                type="button"
                                className={`rounded-lg px-2 py-1.5 text-xs text-white ${
                                  !field.value || isPhoneOpen ? 'opacity-50' : ''
                                }`}
                                style={{ background: '#3B01E0' }}
                                disabled={!field.value || isPhoneVerified}
                                onClick={() => {
                                  setIsPhoneVerified(false)
                                  void sendSMSCode(field.value)
                                }}
                              >
                                {isPhoneVerified ? 'Vérifié' : isPhoneOpen ? 'Code' : 'Envoyer'}
                              </button>
                            }
                            {...field}
                            onChange={(value: string) => {
                              field.onChange(value)
                              setIsPhoneVerified(false)
                            }}
                          />

                          <InlineOtpPopover
                            isOpen={isPhoneOpen}
                            anchorRef={phoneAnchorRef}
                            onClose={() => {
                              setIsPhoneOpen(false)
                            }}
                            phoneLabel={field.value}
                            onSubmitOtp={(otp) => {
                              void verifySMSCode(field.value, otp)
                            }}
                            onResendOtp={async () => {
                              setIsPhoneVerified(false)
                              void sendSMSCode(field.value)
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={addChildForm.control}
                  name="grade"
                  rules={{ required: 'Le niveau est obligatoire' }}
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col">
                      <FormLabel className="text-sm font-medium text-gray-700">Niveau</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Sélectionner un niveau" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="TROISIEME">Troisième</SelectItem>
                            <SelectItem value="SECONDE">Seconde</SelectItem>
                            <SelectItem value="PREMIERE">Première</SelectItem>
                            <SelectItem value="TERMINALE">Terminale</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="gap-4">
                <DialogClose asChild className="flex grow">
                  <Button
                    variant="outline"
                    className="text-sm font-semibold text-blue-800"
                    onClick={handleClose}
                  >
                    Annuler
                  </Button>
                </DialogClose>
                <Button
                  variant="default"
                  className="flex grow text-sm font-semibold"
                  type="submit"
                  disabled={isPending || !isPhoneVerified}
                >
                  {isPending ? 'Ajout en cours...' : 'Ajouter'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  )
}
